/**
 * Checkout rail — README §1 "Checkout rail" (desktop, sticky 380px) and §2 checkout bottom
 * sheet (mobile; `mode="sheet"` renders the panel body only). Every one of the 12 variants
 * in the handoff table, plus `host` / `closed`, decided by `railModel`.
 *
 * Owns quantity / tier / pay-what-you-want / guest name+email / step-2 `CheckoutForm` /
 * waitlist / RSVP / claim / rate flows. Re-homes the logic of EventApplyPage's
 * GuestCheckoutForm, GuestFreeRsvpForm, ApplyForm, ExternalTicketCTA and HostWantedCard.
 *
 * `viewer` = { isAuthenticated, profile, walletBalance, refreshWallet }.
 * `application` = the viewer's application DTO or null; `onApplicationChange(app|null)` is
 * called whenever the rail creates / withdraws one, `onSuccess(app)` after a completed
 * checkout or RSVP.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { eventService, applicationService, ticketService, checkoutService, partyEngineService, promoCodeService } from '../../services';
import { trackEvent } from '../../services/analytics';
import { useDialog } from '../../context/DialogContext';
import CheckoutForm from '../CheckoutForm';
import VerificationFeedbackPanel from './VerificationFeedbackPanel';
import {
  railModel, isTieredEvent, isPwywEvent, resolveTier, flakePenaltyCents, refundRows,
} from './checkoutRailModel';
import {
  Panel, PanelHeader, PanelNote, MoneyBreakdown, SurfaceButton, FieldLabel, Pill, SoftSurface, SOFT, RING_ON,
  MUTED, SUBTLE, GREEN, DANGER,
} from '../surface';
import { money, moneyWhole, dateLabel, capacityLabel } from '../../utils/surfaceFormat';

const PURCHASE_VARIANTS = ['guaranteed', 'starter', 'curated', 'tiered', 'pwyw'];
/** The soft pill input: #F5F5F5, 44px, no border (a transparent 2px keeps the box stable). */
const inputStyle = { border: '2px solid transparent', borderRadius: 999, height: 44, padding: '0 18px', fontSize: 13, width: '100%', background: SOFT.canvas, fontFamily: 'inherit', minWidth: 0, outline: 'none', boxSizing: 'border-box' };
const textareaStyle = { ...inputStyle, height: 'auto', borderRadius: 18, padding: '12px 16px', resize: 'none', lineHeight: 1.5 };
const linkStyle = { fontSize: 10, letterSpacing: '0.1em', color: MUTED };
const softBox = (bg = SOFT.canvas) => ({ background: bg, borderRadius: 18, padding: '12px 14px' });

/**
 * SHARE · SAVE · PORTAL row under the rail panel (desktop only): three 44px pills, SHARE and
 * SAVE white (hover inverts, SAVED stays black), PORTAL on cyan. Signals are Portals now —
 * the word "Signal" never appears.
 */
export function RailShareRow({ onShare, onSave, isBookmarked = false, onPortal, onSignal, onWhite = false, className = '', testId = 'rail-share-row' }) {
  const portal = onPortal || onSignal;
  const base = 'rounded-full font-mono font-bold uppercase transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2';
  const s = { height: 44, fontSize: 10, letterSpacing: '0.14em', border: 'none' };
  // On the canvas (desktop rail) the pills are white; on a white surface (the phone's bottom
  // sheet) they take the canvas grey so they still read as buttons.
  const plain = `${onWhite ? 'bg-[#F5F5F5]' : 'bg-white'} text-black hover:bg-black hover:text-white`;
  return (
    <div className={`grid ${portal ? 'grid-cols-3' : 'grid-cols-2'} gap-[6px] ${className}`} data-testid={testId} data-on-white={onWhite ? 'true' : undefined}>
      <button type="button" onClick={onShare} className={`${base} ${plain}`} style={s} data-testid="rail-share">SHARE</button>
      <button type="button" onClick={onSave} aria-pressed={isBookmarked} className={`${base} ${isBookmarked ? 'bg-black text-white hover:bg-white hover:text-black' : plain}`} style={s} data-testid="rail-save">{isBookmarked ? 'SAVED' : 'SAVE'}</button>
      {/* PORTAL only when there is somewhere for it to go (signed out there isn't) — a pill that does nothing reads as broken. */}
      {portal && <button type="button" onClick={portal} className={`${base} text-black`} style={{ ...s, background: SOFT.cyan }} data-testid="rail-portal">PORTAL</button>}
    </div>
  );
}

/** IF PLANS CHANGE — refund rows computed from the event's flake settings. */
export function RefundCard({ event, rows, className = '' }) {
  const list = rows || refundRows(event);
  if (!list.length) return null;
  return (
    <div className={`flex flex-col bg-white font-mono ${className}`} style={{ padding: '18px 22px', gap: 10 }} data-testid="refund-card">
      <span className="font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.16em', color: MUTED }}>IF PLANS CHANGE</span>
      {list.map((r) => (
        <div key={r.k} className="flex justify-between gap-3" style={{ fontSize: 12 }}>
          <span style={{ color: MUTED }}>{r.k}</span>
          <span className="font-bold text-right">{r.v}</span>
        </div>
      ))}
    </div>
  );
}

/** The #F5F5F5 stepper pill with two 32px white round buttons (36px in the sheet). */
function QtyStepper({ label, value, min, max, onChange, big = false, disabled = false }) {
  const s = big ? 36 : 32;
  const btn = 'rounded-full bg-white font-mono font-bold transition-colors duration-200 hover:bg-black hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-black disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex items-center justify-between gap-[10px]" data-testid="rail-qty">
      <FieldLabel>{label}</FieldLabel>
      <div className="flex items-center rounded-full" style={{ gap: 4, padding: 4, background: SOFT.canvas }}>
        <button type="button" aria-label="Decrease quantity" data-testid="rail-qty-dec" className={btn} style={{ width: s, height: s, fontSize: 15, border: 'none' }} disabled={disabled || value <= min} onClick={() => onChange(Math.max(min, value - 1))}>−</button>
        <span className="text-center font-mono" style={{ fontSize: 14, fontWeight: 800, minWidth: 28 }} data-testid="rail-qty-value">{value}</span>
        <button type="button" aria-label="Increase quantity" data-testid="rail-qty-inc" className={btn} style={{ width: s, height: s, fontSize: 15, border: 'none' }} disabled={disabled || value >= max} onClick={() => onChange(Math.min(max, value + 1))}>+</button>
      </div>
    </div>
  );
}

function ErrorLine({ children }) {
  if (!children) return null;
  return <p className="font-mono" style={{ background: SOFT.redTint, color: DANGER, borderRadius: 14, padding: '8px 12px', fontSize: 11, margin: 0, lineHeight: 1.5 }} data-testid="rail-error">{children}</p>;
}

export default function EventCheckoutRail({
  event,
  application = null,
  viewer = {},
  mode = 'rail',
  onApplicationChange,
  onSuccess,
  onOpenGuestList,
  onShare,
  onSave,
  isBookmarked = false,
  onPortal,
  onSignal,
  goldenTicketsEnabled = true,
  preview = false,
  waitlistPosition = null,
  className = '',
}) {
  const navigate = useNavigate();
  const { showConfirm, showAlert } = useDialog();
  const auth = !!viewer?.isAuthenticated;
  const profile = viewer?.profile || null;
  const sheet = mode === 'sheet';
  const tiered = isTieredEvent(event);
  const pwyw = isPwywEvent(event);
  const curated = event?.mode === 'curated';
  const redirectPath = `/events/${event?.inviteCode || event?.id || ''}`;

  const [quantity, setQuantity] = useState(1);
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [pwywCents, setPwywCents] = useState(null);
  const [pwywInput, setPwywInput] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [message, setMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [agreedToRules, setAgreedToRules] = useState(!!event?.hasAgreedToRules);
  const [useGoldenTicket, setUseGoldenTicket] = useState(false);
  const [pendingApplication, setPendingApplication] = useState(null);
  const [doneApplication, setDoneApplication] = useState(null);
  const [buyingMore, setBuyingMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState(null);
  const [guestWaitlist, setGuestWaitlist] = useState({ mode: false, joined: false, position: null });
  const [ratePanel, setRatePanel] = useState(null);
  const [claimInfo, setClaimInfo] = useState(null);
  const [briefOpen, setBriefOpen] = useState(false);
  const [flashRules, setFlashRules] = useState(false);
  const rulesRef = useRef(null);

  // The application the rail reasons about: the one it just finished, a guest waitlist
  // join (no account, so no DTO), or the page's row.
  const effectiveApp = useMemo(() => {
    if (doneApplication) return doneApplication;
    if (guestWaitlist.joined) return { status: 'waitlisted' };
    return application;
  }, [doneApplication, guestWaitlist.joined, application]);

  // Once the page's row catches up with what the rail finished, drop the local copy.
  useEffect(() => {
    if (doneApplication && application?.id === doneApplication.id) setDoneApplication(null);
  }, [application, doneApplication]);

  // Resume: an existing pending_payment row seeds step 2 directly (email "complete payment" links).
  useEffect(() => {
    if (application?.status === 'pending_payment' && !pendingApplication && !doneApplication) setPendingApplication(application);
  }, [application, pendingApplication, doneApplication]);

  // Host wanted: eligibility + the private brief come from the engine, decided server-side.
  useEffect(() => {
    if (preview || !event?.isHostWanted || !event?.id) return undefined;
    let cancelled = false;
    partyEngineService.getClaimInfo(event.id).then((r) => { if (!cancelled && r.success) setClaimInfo(r.data); });
    return () => { cancelled = true; };
  }, [event?.id, event?.isHostWanted, preview]);

  const model = useMemo(() => railModel({
    event,
    application: effectiveApp,
    viewer,
    quantity,
    selectedTierId,
    pwywCents,
    waitlistPosition: guestWaitlist.position ?? waitlistPosition,
    quote,
    promo: appliedPromo,
    ignoreApplication: buyingMore,
    goldenTicketsEnabled: goldenTicketsEnabled && !buyingMore,
    claimInfo,
  }), [event, effectiveApp, viewer, quantity, selectedTierId, pwywCents, guestWaitlist.position, waitlistPosition, quote, appliedPromo, buyingMore, goldenTicketsEnabled, claimInfo]);

  const { variant } = model;
  const tier = tiered ? resolveTier(event, selectedTierId) : null;
  const isPurchase = PURCHASE_VARIANTS.includes(variant) && !model.gated;
  const interested = application?.status === 'external_interest';

  // Server quote wins over the preview math. Logged-in purchase variants and the anonymous
  // guest checkout both quote; debounced so the stepper doesn't spam the API.
  const wantsQuote = !preview && !pendingApplication && model.unitCents > 0 && (isPurchase || variant === 'guest') && !model.applied;
  const promoCode = appliedPromo?.valid ? appliedPromo.code : null;
  useEffect(() => {
    if (!wantsQuote || !event?.id) { setQuote(null); return undefined; }
    let cancelled = false;
    const id = window.setTimeout(async () => {
      const r = await checkoutService.getQuote(event.id, quantity, promoCode, pwyw ? model.unitCents : null, tier?.id || null);
      if (cancelled) return;
      setQuote(r.success && r.data ? r.data : null);
    }, 300);
    return () => { cancelled = true; window.clearTimeout(id); };
  }, [wantsQuote, event?.id, quantity, promoCode, pwyw, model.unitCents, tier?.id]);

  const refreshWallet = useCallback(async () => { try { await viewer?.refreshWallet?.(); } catch { /* wallet refresh is best-effort */ } }, [viewer]);

  const finish = useCallback((app) => {
    setPendingApplication(null);
    setBuyingMore(false);
    setDoneApplication(app);
    onApplicationChange?.(app);
    onSuccess?.(app);
  }, [onApplicationChange, onSuccess]);

  const scrollToRules = () => {
    rulesRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    setFlashRules(true);
    window.setTimeout(() => setFlashRules(false), 1500);
  };

  // ── logged-in apply → step 2 ───────────────────────────────────────────
  const submitApply = async () => {
    setError('');
    if (model.houseRules.length > 0 && !agreedToRules) { scrollToRules(); return; }
    if (tiered && !tier) { setError('Please choose a ticket option.'); return; }
    if (pwyw && model.pwyw?.belowMin) { setError(`Pledge must be at least ${money(model.pwyw.minCents)} per ticket`); return; }
    const willCharge = model.unitCents > 0;
    setSubmitting(true);
    try {
      const res = await applicationService.createApplication(event.id, {
        message: message.trim(),
        pledgeAmountCents: tiered ? tier.priceCents : model.unitCents,
        useGoldenTicket: useGoldenTicket && curated,
        agreedToHouseRules: agreedToRules,
        quantity,
        useCheckoutFlow: willCharge,
        ticketTierId: tiered ? tier.id : null,
        allowAdditional: buyingMore,
      });
      if (!res.success) {
        const msg = typeof res.error === 'string' ? res.error : res.error?.message || 'Failed to submit application';
        throw new Error(msg);
      }
      trackEvent('application_sent', { eventId: event.id, willCharge, quantity });
      if (res.data?.status === 'waitlisted') {
        // The room filled while they were looking — the server waitlisted them instead.
        setBuyingMore(false);
        onApplicationChange?.(res.data);
      } else if (willCharge) {
        setPendingApplication(res.data);
      } else {
        await refreshWallet();
        finish(res.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckoutSuccess = async (result) => {
    trackEvent('checkout_paid', { eventId: event?.id });
    await refreshWallet();
    let app = { ...pendingApplication, status: result?.status || 'paid' };
    const fresh = await applicationService.getMyApplicationForEvent(event.id);
    if (fresh.success && fresh.data) app = fresh.data;
    finish(app);
  };

  // ── logged-out flows ───────────────────────────────────────────────────
  const guestReady = guestName.trim() && guestEmail.trim();
  const submitGuestCheckout = async () => {
    setError('');
    if (!guestReady) { setError('Your name and email are needed for the ticket.'); return; }
    setSubmitting(true);
    try {
      const response = await ticketService.createGuestCheckout(event.id, {
        name: guestName.trim(),
        email: guestEmail.trim(),
        quantity,
        promoCode: tiered ? null : promoCode,
        ticketTierId: tiered ? (tier?.id || null) : null,
      });
      if (response?.data?.checkoutUrl) { window.location.href = response.data.checkoutUrl; return; }
      setError('Something went wrong. Please try again.');
    } catch (err) {
      const msg = err?.response?.data?.error?.message || '';
      if (msg === 'SOLD_OUT') setGuestWaitlist((w) => ({ ...w, mode: true }));
      else setError(msg || 'Failed to start checkout. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitGuestFree = async () => {
    setError('');
    if (!guestReady) { setError('Your name and email are needed for the ticket.'); return; }
    setSubmitting(true);
    try {
      const response = await ticketService.createGuestFreeTicket(event.id, { name: guestName.trim(), email: guestEmail.trim(), quantity });
      const code = response?.data?.ticketCode;
      const url = response?.data?.ticketUrl;
      if (code) { navigate(`/ticket/${code}`); return; }
      if (url) { window.location.href = url; return; }
      setError('Something went wrong. Please try again.');
    } catch (err) {
      const msg = err?.response?.data?.error?.message || '';
      if (msg === 'SOLD_OUT') setGuestWaitlist((w) => ({ ...w, mode: true }));
      else setError(msg || 'Failed to RSVP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitGuestWaitlist = async () => {
    setError('');
    if (!guestReady) { setError('Your name and email are needed for the waitlist.'); return; }
    setSubmitting(true);
    try {
      const response = await ticketService.joinGuestWaitlist(event.id, { name: guestName.trim(), email: guestEmail.trim(), quantity });
      setGuestWaitlist({ mode: true, joined: true, position: response?.data?.position ?? null });
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'Failed to join waitlist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoChecking(true);
    try {
      const r = await promoCodeService.validate({ eventId: event.id, code, quantity });
      if (r.success && r.data?.valid) setAppliedPromo({ code: code.toUpperCase(), ...r.data });
      else setAppliedPromo({ code: code.toUpperCase(), valid: false, reason: r.data?.reason || r.error || 'Invalid code' });
    } finally {
      setPromoChecking(false);
    }
  };

  // ── external ticketing ─────────────────────────────────────────────────
  const addInterest = async () => {
    setSubmitting(true);
    try {
      const res = await eventService.registerExternalInterest(event.id);
      if (res.success) {
        const appRes = await applicationService.getMyApplicationForEvent(event.id);
        if (appRes.success) onApplicationChange?.(appRes.data || null);
      }
    } finally {
      setSubmitting(false);
    }
  };
  const openExternal = async () => {
    // Open the partner site first so the click isn't eaten by a popup blocker.
    if (event?.externalTicketUrl) window.open(event.externalTicketUrl, '_blank', 'noopener,noreferrer');
    if (auth && !interested && !preview) await addInterest();
  };
  const removeInterest = async () => {
    if (!application?.id) return;
    setSubmitting(true);
    try {
      const res = await applicationService.withdrawApplication(application.id);
      if (res.success) onApplicationChange?.(null);
    } finally {
      setSubmitting(false);
    }
  };

  // ── withdraw / flake ───────────────────────────────────────────────────
  const withdraw = async (kind) => {
    if (!application?.id) return;
    const { committed, penalty, refund } = flakePenaltyCents(event, application);
    const isWaitlist = kind === 'waitlist';
    const ok = await showConfirm({
      title: isWaitlist ? 'LEAVE THE WAITLIST?' : penalty > 0 ? 'FLAKE RULES' : 'WITHDRAW?',
      message: isWaitlist
        ? 'You lose your place in line. No charge either way.'
        : committed > 0
          ? (penalty > 0
            ? `A ${money(penalty)} flake penalty goes to the host. ${money(refund)} of your ${money(committed)} comes back to your wallet.`
            : `Your full ${money(committed)} comes back to your wallet.`)
          : 'Nothing was charged, so nothing is owed.',
      confirmLabel: isWaitlist ? 'LEAVE' : penalty > 0 ? "I'M FLAKING" : 'WITHDRAW',
      variant: 'danger',
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      const res = await applicationService.withdrawApplication(application.id);
      if (res.success) { await refreshWallet(); onApplicationChange?.(null); }
      else setError(res.error || 'Could not withdraw.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── host wanted claim ──────────────────────────────────────────────────
  const claim = async () => {
    const ok = await showConfirm({
      title: 'CLAIM THIS EVENT?',
      message: `It becomes yours to host. Tickets already sold (${money(claimInfo?.party?.pledgedCents || event?.totalPledgedCents || 0)}) move to your wallet's escrow, and you run it like any event you created.`,
      confirmLabel: 'CLAIM IT',
      variant: 'primary',
    });
    if (!ok) return;
    setSubmitting(true);
    const r = await partyEngineService.claim(event.id);
    setSubmitting(false);
    if (!r.success) { await showAlert({ title: 'NOT THIS TIME', message: r.error || 'Could not claim.', type: 'error' }); return; }
    navigate(`/host/events/${event.id}`);
  };

  // ── CTA dispatch ───────────────────────────────────────────────────────
  const primary = async () => {
    if (preview || submitting || model.cta.disabled) return;
    switch (variant) {
      case 'host': navigate(`/host/events/${event.id}`); return;
      case 'ended': setRatePanel('rate'); return;
      case 'hostwanted': if (!auth) navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`); else await claim(); return;
      case 'external': await openExternal(); return;
      case 'held': navigate('/my-tickets'); return;
      case 'closed': navigate('/guest'); return;
      case 'soldout': if (auth) await submitApply(); else await submitGuestWaitlist(); return;
      case 'guest': if (guestWaitlist.mode) await submitGuestWaitlist(); else await submitGuestCheckout(); return;
      case 'free':
        if (auth) await submitApply();
        else if (guestWaitlist.mode) await submitGuestWaitlist();
        else await submitGuestFree();
        return;
      default:
        if (model.gated) { navigate(`/register?redirect=${encodeURIComponent(redirectPath)}`); return; }
        if (model.resume?.kind === 'payment_failed') { navigate(`/recover-payment/${model.resume.application.id}`); return; }
        await submitApply();
    }
  };

  const secondary = async () => {
    if (preview || submitting) return;
    switch (variant) {
      case 'ended': setRatePanel('report'); return;
      case 'hostwanted': onSave?.(); return;
      case 'external': if (interested) await removeInterest(); else await addInterest(); return;
      case 'held': setBuyingMore(true); setQuantity(1); setDoneApplication(null); return;
      case 'soldout': await withdraw('waitlist'); return;
      case 'guest': navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`); return;
      case 'free': navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`); return;
      case 'curated':
        if (model.gated) navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`);
        else if (model.applied === 'pending') await withdraw('application');
        else if (model.goldenTicket?.eligible) setUseGoldenTicket((v) => !v);
        return;
      default:
        if (model.gated) navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`);
    }
  };

  const cta2Label = variant === 'curated' && model.goldenTicket?.eligible && useGoldenTicket ? 'PAY WITHOUT THE GOLDEN TICKET' : model.cta2?.label;

  // ── body pieces ────────────────────────────────────────────────────────
  const showGuestFields = model.guest && !guestWaitlist.joined;
  const showRules = auth && model.houseRules.length > 0 && !model.applied && !pendingApplication && (isPurchase || variant === 'free' || variant === 'soldout');
  const showBreakdown = model.breakdown.show && !guestWaitlist.mode;
  const walletCents = Number(viewer?.walletBalance || 0);
  const guestSignInRow = !auth && ['guest', 'free', 'soldout', 'external', 'ended', 'hostwanted'].includes(variant) && !model.gated;

  const body = pendingApplication ? (
    <div className="flex flex-col" style={{ gap: 14 }} data-testid="rail-step-2">
      <div className="flex items-baseline justify-between">
        <span className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: MUTED }}>STEP 2 OF 2 — PAYMENT</span>
        <span className="font-mono" style={{ fontSize: 11, color: SUBTLE }}>{orderLine(pendingApplication)}</span>
      </div>
      <CheckoutForm
        eventId={event.id}
        applicationId={pendingApplication.id}
        quantity={pendingApplication.quantity || quantity}
        userEmail={profile?.email || profile?.user?.email || ''}
        customPerTicketCents={pwyw ? (pendingApplication.pledgeAmountCents || model.unitCents || event.ticketPriceCents) : null}
        tierId={tiered ? (pendingApplication.ticketTierId || tier?.id || null) : null}
        tierName={tiered ? (tier?.name || null) : null}
        onSuccess={handleCheckoutSuccess}
        onError={(msg) => setError(msg)}
      />
      <ErrorLine>{error}</ErrorLine>
    </div>
  ) : (
    <div className="flex flex-col" style={{ gap: 14 }}>
      {/* price + unit: the big figure left, its note right-aligned */}
      <div className="flex items-end justify-between gap-3">
        <span className="font-mono" style={{ fontSize: sheet ? 28 : 32, fontWeight: 800, lineHeight: 1 }} data-testid="rail-price">{model.price}</span>
        <span className="text-right font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.14em', color: MUTED }} data-testid="rail-price-note">{model.priceNote}</span>
      </div>

      {buyingMore && (
        <div className="flex items-center justify-between">
          <span className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: MUTED }}>ON TOP OF WHAT YOU HOLD</span>
          <button type="button" onClick={() => setBuyingMore(false)} className="font-mono uppercase underline" style={linkStyle}>CANCEL</button>
        </div>
      )}

      {/* tiers */}
      {model.tiers && (
        <div className="flex flex-col gap-2" data-testid="rail-tiers">
          {model.tiers.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={!t.available}
              aria-pressed={t.selected}
              onClick={() => setSelectedTierId(t.id)}
              data-testid={`rail-tier-${t.id}`}
              className="flex w-full items-center justify-between gap-3 text-left font-mono transition-colors duration-200 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
              style={{ padding: '12px 16px', borderRadius: 18, border: '2px solid transparent', boxSizing: 'border-box', background: t.selected ? RING_ON : SOFT.canvas, color: t.selected ? '#fff' : '#000', opacity: t.available ? 1 : 0.45 }}
            >
              <span className="flex min-w-0 flex-col items-start" style={{ gap: 3 }}>
                <span className="font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.14em' }}>{t.name}</span>
                <span style={{ fontSize: 10, opacity: 0.7 }}>{t.note}</span>
              </span>
              <span className="flex-shrink-0" style={{ fontSize: 14, fontWeight: 800 }}>{money(t.priceCents)}</span>
            </button>
          ))}
        </div>
      )}

      {/* pay what you want */}
      {model.pwyw && (
        <div className="flex flex-col gap-2" data-testid="rail-pwyw">
          <FieldLabel>YOU DECIDE — MINIMUM {money(model.pwyw.minCents)}</FieldLabel>
          <div className="flex flex-wrap gap-[6px]">
            {model.pwyw.chips.map((c) => {
              const on = model.pwyw.selectedCents === c;
              return (
                <button
                  key={c}
                  type="button"
                  aria-pressed={on}
                  data-testid={`rail-pwyw-${c}`}
                  onClick={() => { setPwywCents(c); setPwywInput(''); }}
                  className="rounded-full font-mono font-bold transition-colors duration-200 hover:bg-black hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
                  style={{ height: 38, padding: '0 16px', fontSize: 12, border: 'none', background: on ? '#000' : SOFT.canvas, color: on ? '#fff' : '#000' }}
                >
                  {moneyWhole(c)}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono" style={{ fontSize: 13, color: MUTED }}>$</span>
            <input
              type="number"
              inputMode="decimal"
              min={model.pwyw.minCents / 100}
              step="0.01"
              value={pwywInput}
              placeholder={(model.pwyw.suggestedCents / 100).toFixed(2)}
              aria-label="Your amount"
              data-testid="rail-pwyw-custom"
              onChange={(e) => {
                setPwywInput(e.target.value);
                const cents = Math.round(parseFloat(e.target.value || '0') * 100);
                setPwywCents(Number.isNaN(cents) ? null : Math.max(0, cents));
              }}
              onBlur={() => {
                if (pwywInput === '') return;
                const cents = Math.round(parseFloat(pwywInput || '0') * 100);
                if (Number.isNaN(cents) || cents < model.pwyw.minCents) { setPwywCents(model.pwyw.minCents); setPwywInput((model.pwyw.minCents / 100).toFixed(2)); }
              }}
              style={inputStyle}
            />
          </div>
          {model.pwyw.belowMin && <span className="font-mono" style={{ fontSize: 10, color: DANGER }}>Minimum is {money(model.pwyw.minCents)} per ticket.</span>}
        </div>
      )}

      {/* guest identity — no account is created */}
      {showGuestFields && (
        <div className="flex flex-col gap-2" data-testid="rail-guest-fields">
          <input type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Your name" aria-label="Your name" autoComplete="name" data-testid="rail-guest-name" style={inputStyle} />
          <input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="you@email.com" aria-label="Email" autoComplete="email" data-testid="rail-guest-email" style={inputStyle} />
          <span className="font-mono" style={{ fontSize: 10, color: MUTED, lineHeight: 1.5 }}>
            {guestWaitlist.mode
              ? `We'll email you when a spot opens up.`
              : variant === 'soldout'
                ? 'No account needed. We message the waitlist in order.'
                : "Your ticket is emailed. Sign up later with this email and it's waiting for you."}
          </span>
        </div>
      )}

      {/* funding progress (party starter) */}
      {model.funding && (
        <div className="flex flex-col" style={{ gap: 6 }} data-testid="rail-funding">
          <div className="flex items-baseline justify-between gap-3 font-mono">
            <span className="font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.14em', color: MUTED }}>EVENT FUND · {capacityLabel(model.funding.count, model.funding.max)}</span>
            {model.funding.goalType === 'rsvp' ? (
              <span style={{ fontSize: 14, fontWeight: 800 }}>{model.funding.count}<span style={{ fontSize: 10, fontWeight: 500, color: MUTED }}> / {model.funding.max} people</span></span>
            ) : (
              <span style={{ fontSize: 14, fontWeight: 800 }}>{moneyWhole(model.funding.pledgedCents)}<span style={{ fontSize: 10, fontWeight: 500, color: MUTED }}> / {moneyWhole(model.funding.goalCents)}</span></span>
            )}
          </div>
          <div className="overflow-hidden rounded-full" style={{ height: 6, background: SOFT.fundTrack }}>
            <div className="rounded-full" style={{ height: '100%', width: `${model.funding.pct}%`, background: SOFT.magenta }} />
          </div>
          <span className="font-mono" style={{ fontSize: 11, color: MUTED, lineHeight: 1.5 }}>
            {model.unitCents > 0
              ? `Held in escrow until the host greenlights. If it doesn't fund by ${dateLabel(model.funding.deadline)}, every pledge is refunded.`
              : `The host greenlights once enough people are in. RSVP by ${dateLabel(model.funding.deadline)}.`}
          </span>
        </div>
      )}

      {/* held ticket */}
      {model.held && (
        <div className="flex flex-col bg-black text-white" style={{ padding: '14px 16px', gap: 6, borderRadius: 18 }} data-testid="rail-held">
          <span className="font-mono font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.16em', color: SOFT.cyan }}>{model.held.paid || model.held.status === 'accepted' ? "YOU'RE IN" : 'ON THE LIST'}</span>
          <span className="font-mono font-bold" style={{ fontSize: 16 }}>{model.held.tickets} {model.held.tickets === 1 ? 'TICKET' : 'TICKETS'} · {model.held.date}</span>
          <span className="font-mono" style={{ fontSize: 11, color: SUBTLE }}>
            {model.held.paidCents > 0 ? `${model.held.paid ? 'Paid' : 'Pledged'} ${money(model.held.paidCents)} · ` : 'Free · '}order #{model.held.orderRef}
          </span>
        </div>
      )}

      {/* host-wanted private brief */}
      {variant === 'hostwanted' && claimInfo?.party && claimInfo.viewerEligible && (
        <div className="flex flex-col overflow-hidden" style={{ background: SOFT.canvas, borderRadius: 18 }} data-testid="rail-claim-brief">
          {[
            ['VENUE (VERIFIED FOR HIRE)', claimInfo.party.venueName || (claimInfo.party.venueKind ? `a ${claimInfo.party.venueKind}` : 'Your call')],
            ['VENUE RENTAL', `${claimInfo.party.venueCostBasis === 'llm_estimate' ? '≈ ' : ''}${money(claimInfo.party.venueCostEstimateCents || 0)}${claimInfo.party.venueCostBasis === 'llm_estimate' ? ' (ESTIMATE)' : ''}`],
            ['PER HEAD', `≈ ${money(claimInfo.party.perHeadCostCents)}`],
            ['7% FEE AT GOAL', money(claimInfo.party.hostFeeCents)],
            ['YOU CLEAR AT GOAL', money(claimInfo.party.hostMarginCentsAtGoal)],
          ].map(([k, v], i) => (
            <div key={k} className="flex items-center justify-between gap-2 font-mono" style={{ padding: '8px 14px', borderTop: i > 0 ? '2px solid #fff' : 'none', fontSize: 11 }}>
              <span className="uppercase" style={{ color: MUTED, letterSpacing: '0.05em' }}>{k}</span>
              <span className="truncate font-bold">{v}</span>
            </div>
          ))}
          {claimInfo.party.hostBrief && (
            <>
              <button type="button" onClick={() => setBriefOpen((o) => !o)} className="flex w-full items-center justify-between font-mono font-bold uppercase hover:bg-black hover:text-white" style={{ padding: '10px 14px', borderTop: '2px solid #fff', fontSize: 10, letterSpacing: '0.14em' }}>
                <span>HOST BRIEF</span><span>{briefOpen ? '−' : '+'}</span>
              </button>
              {briefOpen && (
                <div className="font-mono" style={{ padding: '10px 14px', borderTop: '2px solid #fff', fontSize: 12, lineHeight: 1.55 }}>
                  {claimInfo.party.venueEvidenceUrl && <p className="uppercase" style={{ margin: '0 0 6px', fontSize: 10, letterSpacing: '0.1em' }}><a href={claimInfo.party.venueEvidenceUrl} target="_blank" rel="noreferrer" className="underline">VENUE'S PRIVATE-HIRE PAGE ↗</a></p>}
                  <p style={{ margin: 0 }}>{claimInfo.party.hostBrief}</p>
                </div>
              )}
            </>
          )}
          <button type="button" onClick={() => navigate('/host/claim')} className="font-mono font-bold uppercase hover:bg-black hover:text-white" style={{ padding: '10px 14px', borderTop: '2px solid #fff', fontSize: 10, letterSpacing: '0.14em', textAlign: 'left' }}>SEE EVERYTHING LOOKING FOR A HOST →</button>
        </div>
      )}

      {/* external add-ons */}
      {variant === 'external' && Array.isArray(event?.addOns) && event.addOns.length > 0 && (
        <div className="flex flex-col gap-2" data-testid="rail-addons">
          <FieldLabel>ADD-ONS FROM THE SELLER</FieldLabel>
          {event.addOns.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 font-mono" style={{ padding: '10px 14px', background: SOFT.canvas, borderRadius: 14 }}>
              <div className="min-w-0">
                <div className="truncate" style={{ fontSize: 12 }}>{a.title}</div>
                {a.price && <div className="uppercase" style={{ fontSize: 10, color: MUTED, letterSpacing: '0.05em' }}>{a.price}</div>}
              </div>
              {a.ticketUrl && <a href={a.ticketUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 font-bold uppercase underline" style={{ fontSize: 10, letterSpacing: '0.1em' }}>GET →</a>}
            </div>
          ))}
        </div>
      )}

      {/* house rules gate */}
      {showRules && (
        <div ref={rulesRef} className="flex flex-col" style={{ ...softBox(), padding: '14px 16px', gap: 8, boxShadow: flashRules ? `0 0 0 3px ${DANGER}` : 'none', transition: 'box-shadow 200ms' }} data-testid="rail-rules">
          <FieldLabel>HOUSE RULES</FieldLabel>
          <ol className="m-0 flex flex-col gap-1 pl-5 font-mono" style={{ fontSize: 11, lineHeight: 1.5 }}>
            {model.houseRules.map((rule) => <li key={rule.id || rule.text}>{rule.text}</li>)}
          </ol>
          <label className="flex cursor-pointer items-center gap-3 font-mono font-bold uppercase" style={{ paddingTop: 10, borderTop: '2px solid #fff', fontSize: 10, letterSpacing: '0.14em' }}>
            <input type="checkbox" checked={agreedToRules} onChange={(e) => setAgreedToRules(e.target.checked)} disabled={submitting} className="h-5 w-5 rounded accent-black" data-testid="rail-rules-agree" />
            I AGREE TO THE HOUSE RULES
          </label>
        </div>
      )}

      {/* golden ticket state / pro upsell */}
      {variant === 'curated' && model.goldenTicket?.eligible && useGoldenTicket && (
        <div className="flex items-center justify-between gap-3 font-mono" style={{ ...softBox(SOFT.amberTint), color: SOFT.amber, fontSize: 11 }} data-testid="rail-golden-on">
          <span className="font-bold uppercase" style={{ letterSpacing: '0.14em' }}>GOLDEN TICKET ON</span>
          <span>{model.goldenTicket.remaining} left · accepted the moment you pay</span>
        </div>
      )}
      {variant === 'curated' && model.goldenTicket?.upsell && (
        <div className="flex flex-col gap-1 font-mono" style={{ ...softBox(), fontSize: 11, color: MUTED }} data-testid="rail-pro-upsell">
          <span>Pro members get 2 Golden Tickets a month — instant acceptance.</span>
          <button type="button" onClick={() => navigate('/membership')} className="self-start font-bold uppercase underline text-black" style={{ fontSize: 10, letterSpacing: '0.1em' }}>UPGRADE TO PRO →</button>
        </div>
      )}

      {/* message to the host */}
      {auth && (isPurchase || variant === 'free') && !model.applied && (
        curated || showMessage ? (
          <div className="flex flex-col gap-1">
            <FieldLabel>{curated ? 'WHO ARE YOU AND WHY THIS NIGHT' : 'NOTE TO THE HOST'}</FieldLabel>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={curated ? 3 : 2} maxLength={500} placeholder={curated ? 'The host reads every one of these.' : 'Optional'} aria-label="Message to the host" data-testid="rail-message" style={textareaStyle} />
          </div>
        ) : (
          <button type="button" onClick={() => setShowMessage(true)} className="self-start font-mono uppercase underline" style={linkStyle}>+ ADD A NOTE TO THE HOST</button>
        )
      )}

      {/* quantity */}
      {model.qty.show && !guestWaitlist.joined && (
        <QtyStepper label={model.qty.label} value={quantity} min={model.qty.min} max={model.qty.max} onChange={setQuantity} big={sheet} disabled={submitting} />
      )}

      {/* money breakdown */}
      {showBreakdown && (
        <MoneyBreakdown
          lines={model.breakdown.lines}
          totalCents={model.breakdown.totalCents}
          walletAppliedCents={model.breakdown.walletAppliedCents}
          after={auth ? (
            <div className="flex items-center justify-between font-mono" style={{ paddingTop: 7, borderTop: `2px solid ${SOFT.chip}`, fontSize: 11, color: MUTED }}>
              <span>WALLET {money(walletCents)}</span>
              <button type="button" onClick={() => navigate('/wallet')} className="font-bold uppercase underline text-black" style={{ fontSize: 10, letterSpacing: '0.1em' }} data-testid="rail-top-up">TOP UP</button>
            </div>
          ) : null}
        />
      )}

      {/* promo code — anonymous checkout only (logged-in codes live in CheckoutForm); hidden when tiered */}
      {variant === 'guest' && !tiered && !guestWaitlist.mode && (
        <div className="flex flex-col gap-1" data-testid="rail-promo">
          {appliedPromo?.valid ? (
            <div className="flex items-center justify-between gap-2 font-mono" style={{ fontSize: 11 }}>
              <span style={{ color: GREEN }}>Code <strong>{appliedPromo.code}</strong> applied — {appliedPromo.discountType === 'percentage' ? `${appliedPromo.discountValue}% off` : `${money(appliedPromo.discountValue)} off`}</span>
              <button type="button" onClick={() => { setAppliedPromo(null); setPromoInput(''); }} className="uppercase underline" style={linkStyle}>REMOVE</button>
            </div>
          ) : (
            <div className="flex items-stretch gap-2">
              <input type="text" value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder="Promo code?" aria-label="Promo code" maxLength={50} autoCapitalize="characters" data-testid="rail-promo-input" style={{ ...inputStyle, height: 38, fontSize: 12, textTransform: 'uppercase' }} />
              <SurfaceButton size="sm" onClick={applyPromo} disabled={promoChecking || !promoInput.trim()} data-testid="rail-promo-apply">{promoChecking ? '…' : 'APPLY'}</SurfaceButton>
            </div>
          )}
          {appliedPromo && !appliedPromo.valid && <span className="font-mono" style={{ fontSize: 10, color: DANGER }}>{appliedPromo.reason || 'Invalid or expired code'}</span>}
        </div>
      )}

      {/* guest waitlist confirmation */}
      {guestWaitlist.joined && (
        <div className="flex flex-col font-mono" style={{ ...softBox(SOFT.amberTint), color: SOFT.amber, gap: 4 }} data-testid="rail-guest-waitlisted">
          <span className="font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.14em' }}>YOU'RE ON THE WAITLIST{guestWaitlist.position ? ` · #${guestWaitlist.position}` : ''}</span>
          <span style={{ fontSize: 11 }}>We'll email {guestEmail} when a spot opens up.</span>
        </div>
      )}

      {/* declined refund note */}
      {model.applied === 'declined' && (
        <div className="flex items-center gap-2 font-mono" style={{ fontSize: 11 }}>
          <Pill tone="redOutline" size="xs">DECLINED</Pill>
          <span style={{ color: MUTED }}>{application?.partyCreditsRefunded ? 'Refunded to your wallet.' : 'Refund on its way to your wallet.'}</span>
        </div>
      )}

      <ErrorLine>{error}</ErrorLine>

      {/* ended: the rate / report flow replaces the CTAs once opened */}
      {variant === 'ended' && ratePanel ? (
        <div className="flex flex-col gap-2" data-testid="rail-rate-panel">
          <VerificationFeedbackPanel event={event} userId={profile?.userId || profile?.id} mobile={sheet} initialReason={ratePanel === 'rate' ? 'attended' : ''} />
          <button type="button" onClick={() => setRatePanel(ratePanel === 'rate' ? 'report' : 'rate')} className="self-start font-mono uppercase underline" style={linkStyle}>
            {ratePanel === 'rate' ? 'SOMETHING WENT WRONG' : 'ACTUALLY, IT WAS FINE'}
          </button>
        </div>
      ) : (
        <>
          <SurfaceButton
            variant={model.cta.fill ? (variant === 'starter' ? 'ring' : 'fill') : 'outline'}
            size="cta"
            fullWidth
            onClick={primary}
            disabled={model.cta.disabled || submitting || (guestWaitlist.mode && guestWaitlist.joined)}
            data-testid="rail-cta"
          >
            {submitting ? 'ONE MOMENT…' : guestWaitlist.mode && !guestWaitlist.joined ? 'JOIN THE WAITLIST' : model.cta.label}
          </SurfaceButton>
          {model.cta2 && !guestWaitlist.mode && (
            <SurfaceButton variant="outline" size="md" fullWidth onClick={secondary} disabled={submitting} data-testid="rail-cta2">
              {cta2Label}
            </SurfaceButton>
          )}
          {/* Withdrawing an accepted-but-not-greenlit spot (the page's FLAKE) */}
          {auth && variant === 'held' && model.applied === 'accepted' && !buyingMore && (
            <button type="button" onClick={() => withdraw('application')} className="self-center font-mono uppercase underline" style={{ ...linkStyle, color: DANGER }} data-testid="rail-flake">
              {event?.flakeFeeMode === 'none' ? 'WITHDRAW' : 'FLAKE (A PENALTY APPLIES)'}
            </button>
          )}
        </>
      )}

      <p className="font-mono" style={{ fontSize: 11, color: MUTED, lineHeight: 1.55, margin: 0 }} data-testid="rail-fine">
        {guestWaitlist.mode && !guestWaitlist.joined ? 'The room is full. Leave your name and we message the waitlist in order when a spot opens.' : model.fine}
      </p>

      {guestSignInRow && (
        <div className="flex items-center gap-3 font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: MUTED }} data-testid="rail-signin-row">
          <button type="button" onClick={() => navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`)} className="underline text-black">SIGN IN</button>
          <span>·</span>
          <button type="button" onClick={() => navigate(`/register?redirect=${encodeURIComponent(redirectPath)}`)} className="underline text-black">CREATE ACCOUNT</button>
        </div>
      )}
    </div>
  );

  if (sheet) {
    // The phone's bottom sheet carries the same SHARE · SAVE · PORTAL row as the desktop rail
    // (Oct 20 2026: share lived only under the rail, so a phone had no way to share from the
    // sheet). The sheet is white, so the pills sit on the canvas grey instead of white.
    return (
      <SoftSurface>
        <div className={`font-mono ${className}`} style={{ padding: '8px 18px 18px' }} data-testid="checkout-rail" data-variant={variant}>
          {body}
          <RailShareRow onShare={onShare} onSave={onSave} isBookmarked={isBookmarked} onPortal={onPortal || onSignal} onWhite className="mt-[14px]" />
        </div>
      </SoftSurface>
    );
  }

  return (
    <SoftSurface>
      <div className={`flex flex-col gap-3 ${className}`} data-testid="checkout-rail" data-variant={variant}>
        <Panel style={{ padding: 22, gap: 16 }}>
          <PanelHeader label={<span data-testid="rail-band">{model.band}</span>} right={model.bandRight || undefined} />
          {body}
          {model.escrow && !pendingApplication && <PanelNote tag="ESCROW">{model.escrow}</PanelNote>}
        </Panel>
        <RailShareRow onShare={onShare} onSave={onSave} isBookmarked={isBookmarked} onPortal={onPortal || onSignal} />
        <RefundCard event={event} rows={model.refund} />
      </div>
    </SoftSurface>
  );
}

function orderLine(app) {
  if (!app?.quantity) return '';
  return `${app.quantity} ${app.quantity === 1 ? 'TICKET' : 'TICKETS'}`;
}
